import { Observable } from 'rxjs';

export interface AccessService {
  hasAccessToFeature(feature: Feature): Observable<boolean>;
  hasAccessToFeatures(features: Feature[]): Observable<boolean>;
  hasAccessToViews(views: Views[]): Observable<Map<Views, boolean>>;
}

// Whitelisted pages list that will NOT be redirected to the restricted page.
// We want to make these pages visible to all partners regardless of tiers. These pages should be read-only.
// Example use case: https://vendasta.jira.com/browse/PRD-864
export const WhiteListedPages = ['/task-manager/reporting'];

// This Views enum contains all of the views in partner center client that users might request access to
// These should be used to decide if something is viewable and/or accessible to a user or not.
// These are NOT a substitute for proper access checks in the backend
// Intended to be used with hasAccessToViews and ViewAccessApiService
export const enum Views {
  partnerAll = 'partner-views',
  salespersonAll = 'salesperson-views',
  newReleases = 'new_releases',
  helpCenter = 'help_center',
  assignedSalespersonCard = 'assigned-salesperson-card',
  inboxAi = 'inbox-ai',
  viewAllCampaigns = 'view-all-campaigns',
  taskManager = 'task-manager',
  ordersCustomFields = 'view-orders-edit-custom-fields',
  viewManageSalesOrders = 'view-manage-sales-orders',
  automations = 'view-automations',
  editAutomations = 'view-edit-automations',
  editCrmLeadScore = 'view-edit-crm-score',
  crmCompanyEdit = 'crm-company-edit',
  crmContactEdit = 'crm-contact-edit',
  companyProfile = 'company-profile-view',
  customizePlatform = 'customize-platform-views',
  manageAdmins = 'manage-admins-views',
  marketplace = 'partner-marketplace-views',
  partnerMarketing = 'partner-marketing-views',
  manageAccounts = 'manage-accounts-views',
  inboxFeature = 'inbox-access',
  allSalesOrdersView = 'view-orders__scope__all',
  ownedSalesOrdersView = 'view-orders__scope__owned',
  allOpportunitiesView = 'view-opportunity__scope__all',
  ownedOpportunitiesView = 'view-opportunity__scope__owned',
  customObjectView = 'custom-objects-view',
}

// This Feature enum contains all of the features that partner center client needs to know about.
// These are Product-Led-Growth (PLG) features, not names of feature-flags.
// https://vendasta.jira.com/wiki/spaces/AC/pages/1898807374/Platform+Subscription+Feature+IDs
export const enum Feature {
  whitelabel = 'white-labeling',
  salesTeam = 'sales-team',
  lists = 'lists',
  brands = 'brands',
  marketingAutomation = 'marketing-automation',
  concierge = 'concierge',
  contentLibrary = 'content-library',
  snapshotWidget = 'snapshot-widget',
  salesAndSuccessCenterAccess = 'ssc-access',
  leaderboard = 'leaderboard',
  businessDirectory = 'business-directory',
  markets = 'markets-access',
  salesOrders = 'sales-orders',
  publicStore = 'public-store',
  localMarketingIndex = 'local-marketing-index',
  executiveReport = 'vbc-executive-report',
  prospects = 'prospects',
  snapshotBulkActivations = 'bulk-snapshot-activations',
  customCampaigns = 'custom-campaigns',
  productWhiteLabeling = 'product-white-labeling',
  cobrandingDisabled = 'co-branding-disabled',
  automations = 'automations',
  pipeline = 'ssc-pipeline',
  advancedAutomationTasks = 'advanced-automations-tasks',
  vendastaPayments = 'vendasta-payments',
  customFields = 'custom-fields',
  customizeBusinessApp = 'customize-business-app',
  serviceAccounts = 'service-accounts',
  metricsInsights = 'metrics-insights',
  multiLocationBusinessApp = 'multi-location-business-app',
  whiteLabelMarketingServices = 'white-label-marketing-services',
  volumeDiscounts = 'volume-discounts',
  premiumApiSupport = 'premium-api-support',
  marketplaceAppPurchase = 'marketplace-app-purchase',
  invoices = 'invoices',
  goals = 'goals',
  inboxSalesContactUs = 'inbox-sales-contact-us',
  inboxAIWebchat = 'inbox-ai-webchat',
  premiumReports = 'premium-reports',
  snapshotReportCustomization = 'snapshot-report-customization',
  taskQueue = 'task-queue',
  crmLeadProspector = 'crm-lead-prospector',
  crmLeadScore = 'crm-lead-scoring',
  crmAISummary = 'crm-ai-summary',
  marketplaceSellProducts = 'marketplace-sell-products',
  managePipeline = 'manage-pipeline',
  singleSignOn = 'single-sign-on',
  translate = 'translate',
  myMeetings = 'my-meetings',
  myProducts = 'my-products',
  customObjects = 'pcc_crm_custom_objects',
}
